import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { isMobile } from 'react-device-detect';
import { cn } from "@plura/shared/utils";
import DatePickerInput from "../../../../molecules/DatePickerInput";
import "./date-screen.css";
import { useFormContext } from "react-hook-form";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";

interface Props {
  id: string;
  step: FormJourneyItem
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const DateScreen = ({ id, step, editorMode, previewMode, active, leadForm, onContentClick, hiddenFields }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const getDateFormat = () => {
    return (step.secondaryContent as 'MMDDYYYY' | 'DDMMYYYY' | 'YYYYMMDD') || 'MMDDYYYY';
  };
  const getDisplayFormatTokens = (): ('MM' | 'DD' | 'YYYY')[] => {
    const f = getDateFormat();
    if (f === 'DDMMYYYY') return ['DD', 'MM', 'YYYY'];
    if (f === 'YYYYMMDD') return ['YYYY', 'MM', 'DD'];
    return ['MM', 'DD', 'YYYY'];
  };
  const generatePlaceholder = () => {
    const orderedFields = getDisplayFormatTokens();
    return orderedFields.join(step?.thirdContent ?? '/');
  };
  const displayFormat = getDisplayFormatTokens().join(step?.thirdContent ?? '/');
  const placeholder = generatePlaceholder();
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  useEffect(() => {
    if ((!editorMode || (editorMode && previewMode)) && active && inputRef.current && !isMobile) {
      inputRef.current.focus();
    }
  }, [active])

  const handleContentClick = (field: 'content' | 'forthContent') => {
    if ((!editorMode || (editorMode && previewMode)) || !onContentClick) return
    onContentClick(field, id)
  }

  return (
    <>
      <div className={cn('questionInner', 'dateItem')}>
        <div>
          <h4
            onClick={() => handleContentClick('content')}
            className={
              cn(
                inputRef.current?.value.length && 'semiFade',
                'question',
              )}
          >
            <motion.span dangerouslySetInnerHTML={{
              __html: dynamicQuestionContent({
                content: step.content,
                score: currentResponseScore,
                editMode: editorMode,
                previewMode: previewMode ?? false,
                hiddenFields,
                journeyItems: leadForm.journeyItems ?? [],
                userResponses
              })
            }} />
          </h4>
          {
            step.forthContent &&
            <p
              onClick={() => handleContentClick('forthContent')}
              className={
                cn(
                  (inputRef.current?.value.length) && 'semiFade',
                  'caption',
                )}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.forthContent,
                    score: currentResponseScore,
                    editMode: editorMode,
                    previewMode: previewMode ?? false,
                    hiddenFields,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses
                  })
                }}
              />
            </p>
          }
        </div>
        <div>
          <motion.div className={'answerInputWrapper'}>
            <AnimatePresence initial={false}>
              <motion.div className={'fieldWrapper'}>
                <DatePickerInput
                  ref={inputRef}
                  id={step.blockId}
                  placeholder={placeholder}
                  displayFormat={displayFormat}
                  className={cn('answerInput')}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </>
  );
};


export default DateScreen